import { motion } from "framer-motion";
import { Target, Rocket, FolderGit2, Briefcase, Award } from "lucide-react";

const deliverables = [
  { Icon: FolderGit2, label: "Real projects from Day 1" },
  { Icon: Rocket, label: "AI-native skills in every subject" },
  { Icon: Briefcase, label: "A guaranteed internship" },
  { Icon: Award, label: "A verified, hire-ready portfolio" },
];

export default function SmruFounder() {
  return (
    <section
      id="founder"
      className="relative overflow-hidden py-20 sm:py-28 bg-gradient-to-b from-white via-aisky/10 to-aipeach/20"
    >
      {/* soft accent blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="float-slow absolute -top-24 -right-24 w-96 h-96 rounded-full bg-aiblue/10 blur-3xl" />
        <div className="float-fast absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-aiorange/10 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-gray-900"
        >
          Learn directly from the youngest Telugu founder dominating the {" "}
          <span className="bg-gradient-to-r from-[#0052FF] to-[#FF6B00] bg-clip-text text-transparent">
          GenAI revolution
          </span>
        </motion.h2>

        <div className="mt-14 grid lg:grid-cols-[20rem_1fr] gap-10 lg:gap-12 items-start">
          {/* LEFT — Sandeep photo + bio */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="mx-auto lg:mx-0 max-w-xs w-full"
          >
            <div className="rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-black/5 bg-gray-50">
              <img
                src="/partners/smru/sandeepKonam.jpg"
                alt="Sandeep Konam — Founder & CEO, AI.Prof"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="mt-5 text-center lg:text-left">
              <p className="font-display font-black text-2xl text-gray-900">
                Sandeep Konam
              </p>
              <p className="text-sm font-semibold text-aiblue">
                Founder &amp; CEO, AI.Prof · Co-Founder, Abridge
              </p>
              <p className="mt-3 text-[15px] text-gray-600 leading-relaxed">
                From rural Nalgonda, Telangana to Carnegie Mellon and a $5.3B AI
                unicorn, Sandeep has come back home to build the system that turns{" "}
                <span className="font-semibold text-gray-900">SMRU engineers</span>{" "}
                into world-class AI professionals.
              </p>
            </div>
          </motion.div>

          {/* RIGHT — our mission message */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-aiblue/10 text-aiblue">
              <Target className="h-3.5 w-3.5" />
              Our Mission
            </span>
            <h3 className="mt-5 font-display font-black text-2xl sm:text-3xl tracking-tight text-gray-900">
              What we want to deliver for every student
            </h3>
            <p className="mt-4 text-gray-600 leading-relaxed text-[15px] sm:text-base">
              We're here to deliver far more than a degree. From{" "}
              <span className="font-semibold text-gray-900">Semester 1</span>, we
              pair SMRU's academic foundation with AI.Prof's industry-grade
              training, equipping every student with the exact skills, real
              projects and verified proof the global tech industry actually hires
              for.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed text-[15px] sm:text-base">
              Our promise is simple: you graduate{" "}
              <span className="font-semibold text-aiblue">
                industry-ready, not just certified
              </span>
              , mentored every step of the way and connected directly to{" "}
              <span className="font-semibold text-gray-900">1,800+ hiring companies</span>.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {deliverables.map(({ Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-3 rounded-2xl bg-white/70 ring-1 ring-gray-100 px-4 py-3 shadow-sm"
                >
                  <Icon className="h-5 w-5 shrink-0 text-aiorange" />
                  <span className="text-sm font-semibold text-gray-800">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
